import React, { useCallback, useEffect, useRef, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { WARRANTY_SUBSCRIPTION_URL } from "../../Shared/constant";
import { Loading, ErrorState } from "../common/WarrantyStates";
import { getPartnerPolicy } from "../common/warrantyApi";
import { formatDate, formatDateTime, formatMinor } from "../common/format";
import {
  ACTION_TEXT,
  benefitKindLabel,
  partnerStateLabel,
  requestStateLabel,
  summaryKeyLabel,
} from "../common/opsLabels";
import OpsActionDialog from "./OpsActionDialog";
import SettleCoverRefundDialog from "./SettleCoverRefundDialog";
import "../warranty.css";

const MONEY_KEYS = new Set(["amountMinor", "taxMinor"]);

// A request or response summary as a key and value list. Every value is text; nothing is HTML.
const SummaryList = ({ summary, currency, label }) => {
  if (!summary || Object.keys(summary).length === 0) {
    return <div className="warranty-muted">None recorded.</div>;
  }
  return (
    <dl className="warranty-facts" aria-label={label}>
      {Object.entries(summary).map(([key, value]) => (
        <React.Fragment key={key}>
          <dt>{summaryKeyLabel(key)}</dt>
          <dd>
            {value === null || value === undefined || value === ""
              ? "-"
              : MONEY_KEYS.has(key) && typeof value === "number"
              ? formatMinor(value, currency)
              : String(value)}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

const Row = ({ label, children }) => (
  <>
    <dt>{label}</dt>
    <dd>{children === null || children === undefined || children === "" ? "-" : children}</dd>
  </>
);

// The side panel of one partner policy: what we know, what was sent and answered, and the one
// action it can take. `onActionDone` lets the screen read its lists and counts again.
const PartnerPolicyDetail = ({ policyId, accessToken, onClose, onActionDone }) => {
  const [view, setView] = useState({ status: "loading", data: null, message: "" });
  // The action being confirmed, copied when it was opened: a reload of the policy (after a refusal)
  // must not change or unmount a dialog that is open. `null` when none is.
  const [acting, setActing] = useState(null);
  // A refusal shown in the panel when the reload shows the action is gone or has changed.
  const [notice, setNotice] = useState("");
  const [queued, setQueued] = useState(false);
  const seq = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // `quiet` reads the policy again without clearing it, so an open confirmation stays open.
  const load = useCallback(
    async (quiet = false) => {
      const mine = ++seq.current;
      if (!quiet) {
        setView({ status: "loading", data: null, message: "" });
      }
      const result = await getPartnerPolicy(accessToken, policyId);
      if (!mounted.current || mine !== seq.current) {
        return;
      }
      if (result.ok) {
        setView({ status: "ready", data: result.data, message: "" });
        return result.data;
      }
      if (!quiet) {
        setView({ status: "error", data: null, message: result.message, notFound: result.notFound });
      }
      return null;
    },
    [accessToken, policyId]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const policy = view.data;

  const openAction = () => {
    setNotice("");
    setActing({
      key: `${policy.id}:${policy.action}`,
      action: policy.action,
      targetId: policy.id,
      amountMinor: policy.amountMinor,
      // The settle names what the cover refund asked for, copied now like the rest of the action.
      askedMinor: policy.lineRefund ? policy.lineRefund.amountMinor : policy.amountMinor,
      currency: policy.currency,
      subject: `${benefitKindLabel(policy.benefitKind)} policy, insurer ${policy.partnerKey || "not set"}`,
    });
  };

  // A definite refusal: read the policy again. If it no longer offers this action, the dialog is
  // closed and the refusal is shown in the panel; otherwise the dialog keeps showing it.
  const refused = async (result) => {
    const current = acting;
    const fresh = await load(true);
    if (!mounted.current || current === null) {
      return;
    }
    if (
      fresh &&
      (fresh.action !== current.action ||
        fresh.amountMinor !== current.amountMinor ||
        fresh.currency !== current.currency)
    ) {
      setActing(null);
      setNotice(result.message);
    }
  };

  return (
    <Offcanvas
      show
      onHide={acting !== null ? () => {} : onClose}
      placement="end"
      className="warranty-side-panel"
      aria-label="Partner policy"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {policy ? `${benefitKindLabel(policy.benefitKind)} policy` : "Partner policy"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {notice && (
          <div className="warranty-banner is-warning" role="alert">
            <span>{notice}</span>
          </div>
        )}
        {view.status === "loading" && <Loading label="Loading the policy" />}
        {view.status === "error" && (
          <ErrorState message={view.message} onRetry={view.notFound ? undefined : () => load(false)} />
        )}
        {policy && (
          <>
            <dl className="warranty-facts">
              <Row label="Insurer">{policy.partnerKey}</Row>
              <Row label="Cover">
                {benefitKindLabel(policy.benefitKind)} ({policy.benefitKey})
              </Row>
              <Row label="State">
                {partnerStateLabel(policy.state)}
                {policy.late && (
                  <>
                    {" "}
                    <span className="warranty-badge is-warning">Late</span>
                  </>
                )}
                {policy.neverActivated && (
                  <>
                    {" "}
                    <span className="warranty-badge is-warning">Never activated</span>
                  </>
                )}
              </Row>
              <Row label="Insurer reference">{policy.partnerReference}</Row>
              <Row label="Policy number">{policy.policyNumber}</Row>
              <Row label="Valid">
                {policy.validFrom || policy.validTo
                  ? `${formatDate(policy.validFrom)} to ${formatDate(policy.validTo)}`
                  : null}
              </Row>
              <Row label="Rejection code">{policy.rejectionCode}</Row>
              <Row label="Rejection message">{policy.rejectionMessage}</Row>
              <Row label="Last error code">{policy.lastErrorCode}</Row>
              <Row label="Paid">{formatMinor(policy.amountMinor, policy.currency)}</Row>
              <Row label="Tax in that">{formatMinor(policy.taxMinor, policy.currency)}</Row>
              <Row label="Request sent">{formatDateTime(policy.requestedAt)}</Row>
              <Row label="Accepted">{formatDateTime(policy.acceptedAt)}</Row>
              <Row label="Issued">{formatDateTime(policy.issuedAt)}</Row>
              <Row label="Document stored">{policy.documentStored ? "Yes" : "No"}</Row>
              <Row label="Cover refund">
                {policy.lineRefund
                  ? `${requestStateLabel(policy.lineRefund.status)}, ${formatMinor(
                      policy.lineRefund.amountMinor,
                      policy.currency
                    )}`
                  : "None"}
              </Row>
            </dl>

            <h6 className="warranty-subhead-plain">What we sent</h6>
            <SummaryList summary={policy.requestSummary} currency={policy.currency} label="Request summary" />
            <h6 className="warranty-subhead-plain">What came back</h6>
            <SummaryList summary={policy.responseSummary} currency={policy.currency} label="Response summary" />

            <div className="warranty-panel-actions">
              {queued ? (
                <span className="warranty-badge is-scheduled">Queued</span>
              ) : policy.action ? (
                <button
                  type="button"
                  className="warranty-inline-btn is-primary"
                  onClick={openAction}
                >
                  {ACTION_TEXT[policy.action]?.label || policy.action}
                </button>
              ) : (
                <span className="warranty-muted">
                  {policy.neverActivated
                    ? "Nothing to do from here. This cover never became active."
                    : "Nothing to do from here."}
                </span>
              )}
              <Link
                className="warranty-inline-btn"
                to={`${WARRANTY_SUBSCRIPTION_URL}/${encodeURIComponent(policy.subscriptionId)}`}
              >
                Open subscription
              </Link>
            </div>
          </>
        )}
      </Offcanvas.Body>
      {acting && acting.action === "settle_cover_refund" && (
        // A cover refund answered with another amount: staff record what the provider shows. Nothing
        // is queued, so the panel reads the policy again instead of showing "Queued".
        <SettleCoverRefundDialog
          key={acting.key}
          show
          targetId={acting.targetId}
          askedMinor={acting.askedMinor}
          currency={acting.currency}
          subject={acting.subject}
          accessToken={accessToken}
          onClose={() => setActing(null)}
          onDone={() => {
            toast.success(`${ACTION_TEXT.settle_cover_refund.done}.`);
            load(true);
            onActionDone(policy);
          }}
          onRefused={refused}
          onUnsure={() => load(true)}
        />
      )}
      {acting && acting.action !== "settle_cover_refund" && (
        <OpsActionDialog
          key={acting.key}
          show
          action={acting.action}
          targetId={acting.targetId}
          amountMinor={acting.amountMinor}
          currency={acting.currency}
          subject={acting.subject}
          accessToken={accessToken}
          onClose={() => setActing(null)}
          onDone={() => {
            setQueued(true);
            onActionDone(policy);
          }}
          onRefused={refused}
          onUnsure={() => load(true)}
        />
      )}
    </Offcanvas>
  );
};

export default PartnerPolicyDetail;
